import {
  PDF_PAGE_HEIGHT_MM,
  PDF_PAGE_WIDTH_MM,
  addSlicedContentImage,
  canvasHeightInMm,
  canvasToJpegDataUrl,
  getContentPaging,
} from "./pagedHtmlPdf";

export const PARECER_DEFAULT_FONT_SIZE = "12";
export const PARECER_PAGE_WIDTH_CSS = "210mm";

export const PARECER_HEADING_CSS_TEXT = {
  H1: "font-size:16pt;font-weight:700;margin:8px 0 16px",
  H2: "font-size:14pt;font-weight:700;margin:16px 0 8px",
  H3: "font-size:13pt;font-weight:700;margin:12px 0 6px",
};

export const PARECER_EDITOR_PROSEMIRROR_SX = {
  outline: "none",
  minHeight: { xs: "200px", sm: "120mm", md: "180mm" },
  fontSize: "12pt",
  lineHeight: 1.5,
  fontFamily: "Arial, sans-serif",
  color: "#1a1a1a",
  py: "20mm",
  "& *": {
    fontSize: "inherit",
  },
  "& h1": {
    fontSize: "16pt",
    fontWeight: 700,
    margin: "8px 0 16px",
  },
  "& h2": {
    fontSize: "14pt",
    fontWeight: 700,
    margin: "16px 0 8px",
  },
  "& h3": {
    fontSize: "13pt",
    fontWeight: 700,
    margin: "12px 0 6px",
  },
  "& p": { margin: "0 0 6px 0" },
  "& ul, & ol": { paddingLeft: "24px", margin: "4px 0 8px" },
  "& li": { marginBottom: "4px" },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    display: "block",
    margin: "8px 0",
  },
  "& mark": { borderRadius: "2px", padding: "0 2px" },
};

export function parseFontSizeValue(cssFontSize) {
  if (!cssFontSize) return null;
  const match = String(cssFontSize)
    .trim()
    .match(/^(\d+(?:\.\d+)?)(px|pt)?$/i);
  if (!match) return null;

  const numericValue = Number(match[1]);
  if (Number.isNaN(numericValue)) return null;

  const unit = (match[2] || "pt").toLowerCase();
  if (unit === "px") {
    return String(Math.round((numericValue * 72) / 96));
  }

  return String(numericValue);
}

export function toFontSizeCss(size) {
  if (!size) return {};
  return { style: `font-size: ${size}pt` };
}

export const PARECER_PDF_BODY_CLASS = "parecer-tecnico-pdf-body";

export const PARECER_PDF_FONT_OVERRIDE_CSS = `
.${PARECER_PDF_BODY_CLASS} { font-size: 12pt; }
.${PARECER_PDF_BODY_CLASS} * { font-size: inherit; }
.${PARECER_PDF_BODY_CLASS} h1 { ${PARECER_HEADING_CSS_TEXT.H1}; }
.${PARECER_PDF_BODY_CLASS} h2 { ${PARECER_HEADING_CSS_TEXT.H2}; }
.${PARECER_PDF_BODY_CLASS} h3 { ${PARECER_HEADING_CSS_TEXT.H3}; }
`.replace(/\s+/g, " ").trim();

export function applyParecerPdfTypography(root) {
  root.classList.add(PARECER_PDF_BODY_CLASS);
  Object.assign(root.style, {
    padding: "10mm 25mm",
    fontFamily: "Arial, sans-serif",
    fontSize: "12pt",
    lineHeight: "1.5",
    color: "#1a1a1a",
    boxSizing: "border-box",
  });

  if (!root.querySelector("style[data-parecer-tipografia]")) {
    const styleElement = document.createElement("style");
    styleElement.setAttribute("data-parecer-tipografia", "true");
    styleElement.textContent = PARECER_PDF_FONT_OVERRIDE_CSS;
    root.prepend(styleElement);
  }

  root.querySelectorAll("h1, h2, h3").forEach((element) => {
    const headingCss = PARECER_HEADING_CSS_TEXT[element.tagName];
    if (headingCss) {
      element.style.cssText = headingCss;
    }
  });

  root.querySelectorAll("*").forEach((element) => {
    if (element.tagName === "STYLE") return;
    if (element.style.fontSize) return;
    element.style.setProperty("font-size", "inherit");
  });
}

export function assembleParecerPdf({
  PdfCtor,
  headerCanvas,
  footerCanvas,
  contentCanvas,
  pageWidthMm = PDF_PAGE_WIDTH_MM,
  pageHeightMm = PDF_PAGE_HEIGHT_MM,
}) {
  const pdf = new PdfCtor({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const headerImg = headerCanvas ? canvasToJpegDataUrl(headerCanvas) : null;
  const headerHeight = headerCanvas
    ? canvasHeightInMm(headerCanvas, pageWidthMm)
    : 0;
  const footerImg = footerCanvas ? canvasToJpegDataUrl(footerCanvas) : null;
  const footerHeight = footerCanvas
    ? canvasHeightInMm(footerCanvas, pageWidthMm)
    : 0;

  const paging = getContentPaging(
    contentCanvas,
    headerHeight,
    footerHeight,
    pageWidthMm,
    pageHeightMm,
  );

  for (let page = 0; page < paging.totalPages; page += 1) {
    if (page > 0) pdf.addPage();
    addSlicedContentImage(
      pdf,
      contentCanvas,
      page,
      paging,
      pageWidthMm,
      headerHeight,
    );
    if (headerImg) {
      pdf.addImage(headerImg, "JPEG", 0, 0, pageWidthMm, headerHeight);
    }
    if (footerImg) {
      pdf.addImage(
        footerImg,
        "JPEG",
        0,
        pageHeightMm - footerHeight,
        pageWidthMm,
        footerHeight,
      );
    }
  }

  return { pdf, paging };
}
